import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { buildRenderableAnswers } from '../../utils/build-renderable-answers'
import { AllergyAnswer } from './render-answers/allergy-answer/AllergyAnswer'
import { ArrayAnswer } from './render-answers/array-answer/ArrayAnswer'
import { DefaultAnswer } from './render-answers/default-answer/DefaultAnswer'
import { FileAnswer } from './render-answers/file-answer/FileAnswer'
import { MedicationAnswer } from './render-answers/medication-answer/MedicationAnswer'
import { MultiSelectAnswer } from './render-answers/multi-select-answer/MultiSelectAnswer'
import { MultiSelectCommentAnswer } from './render-answers/multi-select-comment-answer/MultiSelectCommentAnswer'

import type { QuestionnaireResponse } from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters } from 'index'
import type { ReactNode } from 'react'
import type { AllergyIntolerances, AllergyProfile, MedicationScheme } from '@/modules/app/types/HealthParameter.types'
import type { QuestionAnswer, RenderableAnswer } from '../../types'
import type { QuestionnaireResponseWithIncludes } from '../../types/questionnaire.types'

type Props = {
  questionnaire: IQuestionnaireWithHealthParameters
  questionnaireResponse: QuestionnaireResponse | QuestionnaireResponseWithIncludes
  editable?: boolean

  onEditAnswer?: (linkId: string) => void
}

export const QuestionnaireAnswers = ({ questionnaire, questionnaireResponse, editable, onEditAnswer }: Props) => {
  const renderableAnswers = useMemo(
    () =>
      buildRenderableAnswers({
        questionnaire,
        questionnaireResponse,
        editable: editable && !!onEditAnswer,
      }),
    [questionnaire, questionnaireResponse, editable, onEditAnswer],
  )

  return (
    <StContainer>
      {renderableAnswers.map((item) => (
        <StQuestionAnswerItem key={item.linkId}>
          <StAnswerContent>{renderAnswer(item)}</StAnswerContent>
          {item.isEditable && onEditAnswer ? (
            <FlexColumn justifyContent='flex-start' alignItems='center' style={{ height: '100%', flexShrink: 0 }}>
              <StEditIcon icon={['far', 'pen']} onClick={() => onEditAnswer(item.linkId)} />
            </FlexColumn>
          ) : null}
        </StQuestionAnswerItem>
      ))}
    </StContainer>
  )
}

const StContainer = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StQuestionAnswerItem = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StAnswerContent = styled.div`
  flex: 1;
  min-width: 0;
`

const StEditIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
`

const renderAnswer = (item: RenderableAnswer): ReactNode => {
  // Fallback for historical answers whose question type was changed after
  // submission — the response FK points to the live questionnaire, so the
  // value shape may not match the current question type.
  if (item.kind === 'MultiSelect' || item.kind === 'MultiSelectComment') {
    const v = item.answer?.value
    if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      if ('allergies' in v) return renderAnswer({ ...item, kind: 'Allergy' })
      if ('medications' in v) return renderAnswer({ ...item, kind: 'Medication' })
    }
  }

  switch (item.kind) {
    case 'File': {
      return <FileAnswer title={item.title} description={item.description} files={item.answer.files} />
    }
    case 'Array': {
      return (
        <ArrayAnswer
          title={item.title}
          description={item.description}
          fields={item.question.fields || []}
          value={item.answer.value}
        />
      )
    }
    // Tech debt: MultiSelect resolves labels client-side from
    // question.answerOption for backwards compatibility — old persisted records
    // store raw option values in answer.text (e.g. "1") instead of resolved
    // labels. Once a data migration backfills correct text for all existing
    // answers, this can be replaced by rendering answer.text directly.
    case 'MultiSelect': {
      const answerValues: string[] = (() => {
        if (typeof item.answer.value === 'string') {
          try {
            const parsed = JSON.parse(item.answer.value)
            return Array.isArray(parsed) ? parsed : []
          } catch {
            return []
          }
        }
        return Array.isArray(item.answer.value) ? item.answer.value : []
      })()

      return (
        <MultiSelectAnswer
          title={item.title}
          description={item.description}
          answerOptions={item.question.answerOption ?? []}
          answers={answerValues ?? []}
        />
      )
    }
    // Tech debt: MultiSelectComment resolves labels client-side from
    // question.answerOption for backwards compatibility — old persisted records
    // store raw option values in answer.text instead of resolved labels. Once a
    // data migration backfills correct text for all existing answers, this can
    // be replaced by rendering answer.text directly.
    case 'MultiSelectComment': {
      return (
        <MultiSelectCommentAnswer
          title={item.title}
          description={item.description}
          answerOptions={item.question.answerOption ?? []}
          value={item.answer.value}
        />
      )
    }
    case 'Allergy': {
      const value = item.answer?.value

      // New scheme format (AllergyProfile with structured allergies)
      if (value && typeof value === 'object' && !Array.isArray(value) && 'schemeStatus' in value) {
        const allergyScheme = value as AllergyProfile
        return <AllergyAnswer title={item.title} description={item.description} allergyScheme={allergyScheme} />
      }

      // Legacy format (synops + user observation)
      const storedAllergyData =
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as {
              synops: AllergyIntolerances[]
              user: string
            })
          : undefined

      return <AllergyAnswer title={item.title} description={item.description} storedAllergyData={storedAllergyData} />
    }
    case 'QuickReplies': {
      const answerOption = item.question.answerOption?.find((option) => option.value === item.answer.value)
      const mappedAnswer = answerOption ? { ...item.answer, text: answerOption.label } : item.answer

      return <DefaultAnswer title={item.title} description={item.description} answer={mappedAnswer} />
    }
    case 'Medication': {
      const value = item.answer.value

      // Legacy format: [{name, frequency, intake}]
      if (Array.isArray(value)) {
        const NO_MEDICATION_NAMES = new Set(['geen thuismedicatie', 'ik neem geen thuismedicatie'])
        const isNoMedication =
          value.length === 1 &&
          NO_MEDICATION_NAMES.has(((value[0] as { name?: string })?.name ?? '').toLowerCase().trim())

        if (value.length === 0 || isNoMedication) {
          return (
            <MedicationAnswer
              title={item.title}
              description={item.description}
              medications={[]}
              schemeStatus='confirmed-empty'
            />
          )
        }

        const legacyMedications = (value as { name: string; frequency: string; intake: string }[]).map((m) => ({
          synopsId: null,
          diffStatus: 'unchanged' as const,
          name: m.name,
          code: null,
          source: 'patient',
          status: 'recorded',
          updatedAt: '',
          dosage: [
            {
              text: m.frequency || m.intake ? `${m.frequency} - ${m.intake}` : '',
              timing: null,
            },
          ],
        }))

        return (
          <MedicationAnswer
            title={item.title}
            description={item.description}
            medications={legacyMedications}
            schemeStatus='recorded'
          />
        )
      }

      // New format: { schemeStatus, medications: MedicationStatement[] }
      const medicationScheme = value as MedicationScheme
      const medications = (medicationScheme?.medications ?? []).filter((m) => m.diffStatus !== 'deleted')
      const schemeStatus = medicationScheme?.schemeStatus ?? 'unknown'

      // When the medication list is empty but raw text exists (e.g. legacy data),
      // fall back to rendering the raw text via DefaultAnswer — mirroring the
      // backoffice behaviour in QuestionnaireResponsePreview.tsx.
      if (medications.length === 0 && item.answer.text && schemeStatus !== 'confirmed-empty') {
        return (
          <DefaultAnswer
            title={item.title}
            description={item.description}
            answer={item.answer}
            medicationStyleContent
          />
        )
      }

      return (
        <MedicationAnswer
          title={item.title}
          description={item.description}
          medications={medications}
          schemeStatus={schemeStatus}
        />
      )
    }
    case 'Date': {
      let formattedDate = item.answer.text ?? item.answer.value ?? ''

      try {
        const dateValue = item.answer.text ?? item.answer.value
        if (dateValue) {
          const date = new Date(dateValue)
          if (!Number.isNaN(date.getTime())) {
            formattedDate = format(date, 'dd/MM/yyyy')
          }
        }
      } catch (error) {
        console.error('Error formatting date:', error)
        formattedDate = item.answer.text ?? item.answer.value ?? ''
      }

      const formattedAnswer = {
        ...item.answer,
        text: formattedDate,
        value: formattedDate,
      } as QuestionAnswer

      return <DefaultAnswer title={item.title} description={item.description} answer={formattedAnswer} />
    }
    default: {
      return <DefaultAnswer title={item.title} description={item.description} answer={item.answer} />
    }
  }
}
